import { axiosClient } from '../Service/axiosClient';

/**
 * Capability store for the UI (role capabilities, Phase 1b).
 * Loads the caller's capabilities once from commonapi (GET /auth/me/capabilities) and keeps them in a module
 * store, so any component (and non-React code) can ask can('module.resource.action').
 *
 * The UI is a convenience only: every API enforces the same capabilities on the server.
 * Behind the flag REACT_APP_CAPABILITY_UI=on. With the flag off nothing is fetched and can() returns true everywhere.
 */

export type CapabilityState = {
    loaded: boolean;
    /** true when the load failed (flag on); can() then denies, except for alumni / student users */
    error: boolean;
    /** Reserved: the server always sends false (no wildcard role since decision D-30); can() still honours it if it is ever true */
    wildcard: boolean;
    roleCodes: string[];
    capabilities: ReadonlySet<string>;
    loginMode: string;
};

type CapabilityResponse = {
    roleCodes: string[];
    wildcard: boolean;
    capabilities: string[];
    loginMode: string;
    cacheTtlSeconds: number;
};

type CachedEntry = { tokenKey: string; expiresAt: number; data: CapabilityResponse };

const CACHE_KEY = 'capabilities-cache';
const DEFAULT_CACHE_SECONDS = 600;

const emptyState = (loaded: boolean, error = false): CapabilityState => ({
    loaded,
    error,
    wildcard: false,
    roleCodes: [],
    capabilities: new Set<string>(),
    loginMode: '',
});

let state: CapabilityState = emptyState(false);
const listeners = new Set<() => void>();
let inflight: { tokenKey: string; promise: Promise<CapabilityState> } | null = null;
// which token the current state belongs to, when it must be refreshed, and the last failed load (retry pause)
let stateKey: string | null = null;
let stateExpiresAt = 0;
let lastFailure: { key: string; at: number } | null = null;
const RETRY_AFTER_FAILURE_MS = 30 * 1000;

export const isCapabilityUiEnabled = (): boolean => process.env.REACT_APP_CAPABILITY_UI === 'on';

export const getCapabilityState = (): CapabilityState => state;

/** For React (useSyncExternalStore) and other subscribers. Returns the unsubscribe function. */
export const subscribeCapabilities = (listener: () => void): (() => void) => {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
};

const setState = (next: CapabilityState) => {
    state = next;
    listeners.forEach((l) => l());
};

// Same lookup order as axiosClient: sessionStorage first, then localStorage.
const readToken = (): string | null => sessionStorage.getItem('user-jwt-token') || localStorage.getItem('user-jwt-token') || null;

// "sub:iat" of the JWT. Login, auto-login, institute switch and impersonation all issue a new token,
// so a new key means a reload. Returns null if the token cannot be read (then nothing is cached).
const tokenKeyOf = (token: string): string | null => {
    try {
        const part = token.split('.')[1];
        if (!part) return null;
        const base64 = part.replace(/-/g, '+').replace(/_/g, '/');
        const claims = JSON.parse(atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, '=')));
        if (!claims || claims.sub === undefined || claims.iat === undefined) return null;
        return `${claims.sub}:${claims.iat}`;
    } catch (_) {
        return null;
    }
};

const readCache = (tokenKey: string): { data: CapabilityResponse; expiresAt: number } | null => {
    try {
        const raw = sessionStorage.getItem(CACHE_KEY);
        if (!raw) return null;
        const entry: CachedEntry = JSON.parse(raw);
        if (entry.tokenKey !== tokenKey || entry.expiresAt <= Date.now()) return null;
        const data = normalize(entry.data);
        return data ? { data, expiresAt: entry.expiresAt } : null;
    } catch (_) {
        return null;
    }
};

const writeCache = (tokenKey: string, data: CapabilityResponse, expiresAt: number) => {
    try {
        const entry: CachedEntry = { tokenKey, expiresAt, data };
        sessionStorage.setItem(CACHE_KEY, JSON.stringify(entry));
    } catch (_) {
        /* storage full or blocked: the store still works, it just refetches next time */
    }
};

// Accepts only the documented shape; anything else is treated as a failed load.
function normalize(data: any): CapabilityResponse | null {
    if (!data || !Array.isArray(data.capabilities) || !Array.isArray(data.roleCodes)) return null;
    return {
        roleCodes: data.roleCodes.filter((r: any) => typeof r === 'string'),
        wildcard: data.wildcard === true,
        capabilities: data.capabilities.filter((c: any) => typeof c === 'string'),
        loginMode: typeof data.loginMode === 'string' ? data.loginMode : 'FULL_ACCESS',
        cacheTtlSeconds: Number(data.cacheTtlSeconds) || DEFAULT_CACHE_SECONDS,
    };
}

const toState = (data: CapabilityResponse): CapabilityState => ({
    loaded: true,
    error: false,
    wildcard: data.wildcard,
    roleCodes: data.roleCodes,
    capabilities: new Set(data.capabilities),
    loginMode: data.loginMode,
});

/** Forget everything (logout, institute switch before the new token is known). */
export const clearCapabilities = () => {
    inflight = null;
    stateKey = null;
    stateExpiresAt = 0;
    lastFailure = null;
    try {
        sessionStorage.removeItem(CACHE_KEY);
    } catch (_) {
        /* ignore */
    }
    setState(emptyState(false));
};

/**
 * Load the capabilities of the current token. Cheap to call often (the app calls it at start and after every
 * navigation, because login, institute switch and impersonation all store a new token and then navigate):
 * - same token and still fresh: returns the current state, no work, no re-render
 * - same token, cache older than cacheTtlSeconds: refreshes (a failed refresh keeps the old list)
 * - new token: the previous user's state is dropped at once, then loaded (sessionStorage copy or one request)
 * - a failed load is not retried for 30 seconds
 * Never throws: a failed first load sets state.error (fail closed when the flag is on).
 */
export const loadCapabilities = async (): Promise<CapabilityState> => {
    if (!isCapabilityUiEnabled()) {
        if (!state.loaded) setState(emptyState(true));
        return state;
    }

    const token = readToken();
    if (!token) {
        if (state.loaded || state.error) clearCapabilities();
        return state;
    }

    const tokenKey = tokenKeyOf(token);
    const key = tokenKey || token;
    const sameUser = stateKey === key;

    if (sameUser && state.loaded && !state.error && Date.now() < stateExpiresAt) return state;
    if (sameUser && state.error && lastFailure && lastFailure.key === key && Date.now() - lastFailure.at < RETRY_AFTER_FAILURE_MS) return state;
    if (inflight && inflight.tokenKey === key) return inflight.promise;

    if (tokenKey) {
        const cached = readCache(tokenKey);
        if (cached) {
            stateKey = key;
            stateExpiresAt = cached.expiresAt;
            lastFailure = null;
            setState(toState(cached.data));
            return state;
        }
    }

    // a different token means a different user, institute or impersonation: never show the previous list
    if (!sameUser && (state.loaded || state.error)) setState(emptyState(false));

    const entry: { tokenKey: string; promise: Promise<CapabilityState> } = { tokenKey: key, promise: Promise.resolve(state) };
    inflight = entry;
    entry.promise = (async (): Promise<CapabilityState> => {
        try {
            const response = await axiosClient().get(`${process.env.REACT_APP_COMMON_API}/auth/me/capabilities`);
            const data = normalize(response.data);
            if (!data) throw new Error('unexpected /auth/me/capabilities response');
            // the user may have logged out or switched institute while the request was running
            if (inflight !== entry) return state;
            const expiresAt = Date.now() + data.cacheTtlSeconds * 1000;
            if (tokenKey) writeCache(tokenKey, data, expiresAt);
            inflight = null;
            stateKey = key;
            stateExpiresAt = expiresAt;
            lastFailure = null;
            setState(toState(data));
        } catch (err) {
            if (inflight !== entry) return state;
            console.error('Could not load capabilities', err);
            inflight = null;
            lastFailure = { key, at: Date.now() };
            if (stateKey === key && state.loaded && !state.error) {
                // a refresh failed: keep the list we already have and try again later
                stateExpiresAt = Date.now() + RETRY_AFTER_FAILURE_MS;
            } else {
                stateKey = key;
                stateExpiresAt = 0;
                setState(emptyState(true, true));
            }
        }
        return state;
    })();
    return entry.promise;
};

// Users who only have the self-service roles (alumni / student) are not capability-gated in the UI:
// their screens are not part of the admin matrix, and the server decides per route (allowSelfService).
const SELF_SERVICE_ROLES = ['ALUMNI', 'STUDENT'];
const isSelfServiceOnly = (roleCodes: string[]) => roleCodes.length > 0 && roleCodes.every((r) => SELF_SERVICE_ROLES.includes(r));

/**
 * Does the logged-in user have this capability?
 * - flag off: true (the UI does not change)
 * - not loaded yet, or the load failed: false (fail closed), except alumni / student users
 * - alumni / student only: true
 * - wildcard (reserved, the server sends false): true
 */
export const can = (key: string, current: CapabilityState = state): boolean => {
    if (!isCapabilityUiEnabled()) return true;
    if (!current.loaded) return false;
    if (isSelfServiceOnly(current.roleCodes)) return true;
    if (current.error) return false;
    if (current.wildcard) return true;
    return current.capabilities.has(key);
};

/** True when the user has at least one of the capabilities (an empty list is false). */
export const canAny = (keys: string[], current: CapabilityState = state): boolean => {
    if (!isCapabilityUiEnabled()) return true;
    return keys.some((k) => can(k, current));
};
